import React, { useCallback, useState } from 'react';

import NiceModal, { useModal } from '@ebay/nice-modal-react';

import DoubleTeamsRemoveDialog from 'components/dialogs/doubles/DoubleTeamsRemoveDialog';
import useMutateTournament from 'hooks/tournament/useMutateTournament';
import { type PairParticipant } from 'typedefs/pair';

interface ModalProps {
  eventId: string;
  pair: [PairParticipant, PairParticipant];
}

const Modal = NiceModal.create(({ eventId, pair, ...props }: ModalProps) => {
  const modal = useModal();
  const [destroyPair] = useMutateTournament('destroyPair');
  const [removePair, setRemovePair] = useState(pair);

  const handleDestroyPair = useCallback(
    (...args: any) => {
      destroyPair(eventId, ...args);
    },
    [destroyPair, eventId],
  );

  const handleChangeCheckbox = useCallback(
    (event) => {
      const newRemovePair = removePair.map((participant) =>
        participant.individualParticipantId === event.target.value
          ? {
              ...participant,
              checked: !participant.checked,
            }
          : participant,
      );
      setRemovePair(newRemovePair as [PairParticipant, PairParticipant]);
    },
    [removePair],
  );

  const handleConfirmRemove = useCallback(() => {
    if (removePair) {
      const checkedParticipantIds =
        removePair
          ?.filter((participant) => participant.checked)
          .map((participant) => participant.individualParticipantId) || [];
      handleDestroyPair(removePair[0].participantId, checkedParticipantIds);
      setRemovePair(undefined);
    }
    modal.resolve();
    modal.hide();
  }, [removePair, modal, handleDestroyPair]);

  const handleCancelRemove = useCallback(() => {
    setRemovePair(undefined);
    modal.hide();
  }, [modal]);

  return (
    <DoubleTeamsRemoveDialog
      open={modal.visible}
      removePair={removePair}
      onCancel={handleCancelRemove}
      onChangeCheckbox={handleChangeCheckbox}
      onExited={modal.remove}
      onRemove={handleConfirmRemove}
      {...props}
    />
  );
});

export const useDoubleTeamsRemoveDialog = () =>
  useCallback(
    (eventId: ModalProps['eventId'], pair: ModalProps['pair'], props?: Record<string, unknown>) =>
      NiceModal.show(Modal, { eventId, pair, ...props }),
    [],
  );
